import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Activity, Database, Mail, Server, HardDrive, Bug, X, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2, Download, Upload, ShieldAlert, QrCode, Save, Smartphone, Wifi, Map as MapIcon, Route } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import MqttPanel from "./MqttPanel";
import DiagnosticsPanel from "./DiagnosticsPanel";
import VoiceTranslationSettings from "./VoiceTranslationSettings";
import AnalyticsPanel from "./AnalyticsPanel";
import { refreshBranding } from "@/lib/branding";

function fmtSecs(s) {
  if (!s && s !== 0) return "—";
  if (s < 60) return `${s.toFixed(0)}s`;
  if (s < 3600) return `${(s / 60).toFixed(1)} min`;
  if (s < 86400) return `${(s / 3600).toFixed(1)} h`;
  return `${(s / 86400).toFixed(1)} d`;
}

function StatCard({ label, value, tone = "default", testid }) {
  const toneMap = {
    default: "border-border",
    good: "border-emerald-500/40 bg-emerald-500/5",
    warn: "border-amber-500/40 bg-amber-500/5",
    bad: "border-destructive/40 bg-destructive/5",
  };
  return (
    <div className={`border ${toneMap[tone]} p-4`} data-testid={testid}>
      <div className="overline text-primary/50 text-[10px] mb-1">{label}</div>
      <div className="font-display text-xl truncate">{value}</div>
    </div>
  );
}

export default function PlatformToolsPanel() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [logLevel, setLogLevel] = useState("all");
  const [logLines, setLogLines] = useState(200);
  const [logLoading, setLogLoading] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [bugs, setBugs] = useState([]);
  const [bugForm, setBugForm] = useState({ title: "", description: "", severity: "medium" });
  const [creatingBug, setCreatingBug] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const restoreInputRef = useRef(null);
  // Guest-mobile URL (rendered as QR on the iPad cabin view)
  const [mobileUrl, setMobileUrl] = useState("");
  const [mobileUrlLoaded, setMobileUrlLoaded] = useState(false);
  const [mobileUrlSaving, setMobileUrlSaving] = useState(false);
  // Guest connection activity (Developer feed)
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFilter, setActivityFilter] = useState("all"); // all | connected | never | mobile
  // Journey-map switches — controlled from Platform Tools and consumed by
  // the shared cabin iPad + personal /journey timeline via `useBranding`.
  const [portMapOn, setPortMapOn] = useState(true);
  const [routeMapOn, setRouteMapOn] = useState(true);
  const [settings, setSettings] = useState(null);
  const [mapSwitchesSaving, setMapSwitchesSaving] = useState(false);

  const loadHealth = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/platform/health"); setHealth(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Health load failed"); }
    finally { setLoading(false); }
  }, []);

  const loadLogs = useCallback(async () => {
    setLogLoading(true);
    try {
      const { data } = await api.get("/admin/platform/logs", { params: { level: logLevel, lines: logLines } });
      setLogs(data.entries || []);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Log load failed"); }
    finally { setLogLoading(false); }
  }, [logLevel, logLines]);

  const loadBugs = useCallback(async () => {
    try { const { data } = await api.get("/admin/platform/bug-reports"); setBugs(data || []); }
    catch (e) { console.debug("[PlatformTools] bug-reports load failed", e); }
  }, []);

  useEffect(() => { loadHealth(); loadBugs(); }, [loadHealth, loadBugs]);
  useEffect(() => { if (logsOpen) loadLogs(); }, [logsOpen, loadLogs]);

  // Load the current guest-mobile URL from /public/settings (any role can read)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/settings");
        setMobileUrl(data.guest_mobile_url || "");
        // Preload the two map switches — defaults to ON so an empty
        // settings doc doesn't silently strip maps out.
        setPortMapOn(data.journey_port_map_enabled !== false);
        setRouteMapOn(data.journey_route_map_enabled !== false);
        setSettings(data || {});
      } catch (e) { console.debug("[PlatformTools] public settings load failed", e); }
      finally { setMobileUrlLoaded(true); }
    })();
  }, []);

  const saveMapSwitch = async (patch) => {
    setMapSwitchesSaving(true);
    try {
      // Fetch the current settings first so a partial toggle doesn't
      // silently overwrite unrelated fields (SettingsIn is a full-blob
      // PUT that fills in Pydantic defaults for any missing keys).
      let current = {};
      try {
        const cur = await api.get("/admin/settings");
        current = cur.data || {};
      } catch { /* fall back to local state */ }
      const next = {
        ...current,
        journey_port_map_enabled: portMapOn,
        journey_route_map_enabled: routeMapOn,
        ...patch,
      };
      await api.put("/admin/settings", next);
      if ("journey_port_map_enabled" in patch) setPortMapOn(patch.journey_port_map_enabled);
      if ("journey_route_map_enabled" in patch) setRouteMapOn(patch.journey_route_map_enabled);
      if ("mgmt_mask_names" in patch) setSettings((s) => ({ ...(s || {}), mgmt_mask_names: patch.mgmt_mask_names }));
      await refreshBranding();
      toast.success("Setting updated");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not save switch");
    } finally { setMapSwitchesSaving(false); }
  };

  const saveMobileUrl = async () => {
    setMobileUrlSaving(true);
    try {
      const { data } = await api.put("/admin/platform/guest-mobile-url", { guest_mobile_url: mobileUrl.trim() });
      setMobileUrl(data.guest_mobile_url || "");
      toast.success(data.guest_mobile_url ? "Guest mobile URL saved" : "Guest mobile URL cleared — QR hidden");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not save URL");
    } finally { setMobileUrlSaving(false); }
  };

  const loadActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const { data } = await api.get("/admin/platform/guest-activity");
      setActivity(data.items || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not load activity");
    } finally { setActivityLoading(false); }
  }, []);

  useEffect(() => { loadActivity(); const t = setInterval(loadActivity, 30000); return () => clearInterval(t); }, [loadActivity]);

  const filteredActivity = useMemo(() => {
    if (activityFilter === "connected") return activity.filter((a) => !!a.last_login_at);
    if (activityFilter === "never") return activity.filter((a) => !a.last_login_at);
    if (activityFilter === "mobile") return activity.filter((a) => a.has_mobile);
    return activity;
  }, [activity, activityFilter]);

  const activityCounts = useMemo(() => ({
    total: activity.length,
    connected: activity.filter((a) => !!a.last_login_at).length,
    mobile: activity.filter((a) => a.has_mobile).length,
    ipad_only: activity.filter((a) => a.last_login_kind === "ipad").length,
  }), [activity]);

  const fmtWhen = (iso) => {
    if (!iso) return "—";
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return "just now";
    if (ms < 3600_000) return `${Math.round(ms / 60_000)}m ago`;
    if (ms < 86400_000) return `${Math.round(ms / 3600_000)}h ago`;
    return `${Math.round(ms / 86400_000)}d ago`;
  };
  const kindBadge = (kind, device) => {
    if (!kind) return <span className="text-muted-foreground text-[10px]">—</span>;
    const cls = kind === "mobile-resume" ? "bg-accent/15 text-accent border-accent/30"
              : kind === "personal" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"
              : "bg-primary/10 text-primary border-primary/30";
    return <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${cls}`}>{kind} · {device || "?"}</span>;
  };

  const submitBug = async () => {
    if (!bugForm.title.trim()) { toast.error("Title required"); return; }
    setCreatingBug(true);
    try {
      await api.post("/admin/platform/bug-reports", bugForm);
      toast.success("Bug report filed");
      setBugForm({ title: "", description: "", severity: "medium" });
      loadBugs();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not file bug"); }
    finally { setCreatingBug(false); }
  };

  const closeBug = async (r) => {
    if (!window.confirm("Close this report?")) return;
    try { await api.post(`/admin/platform/bug-reports/${r.id}/close`); loadBugs(); } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not close report"); }
  };
  const deleteBug = async (r) => {
    if (!window.confirm("Delete this report permanently?")) return;
    try { await api.delete(`/admin/platform/bug-reports/${r.id}`); loadBugs(); } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not delete report"); }
  };

  const downloadBackup = async (format = "json") => {
    setDownloading(true);
    try {
      const resp = await api.get("/admin/platform/backup/export", {
        params: { format },
        responseType: "blob",
      });
      const disposition = resp.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(disposition);
      const ext = format === "zip" ? "zip" : "json";
      const filename = (match && match[1]) || `yachtspa-backup-${Date.now()}.${ext}`;
      const mime = format === "zip" ? "application/zip" : "application/json";
      const url = URL.createObjectURL(new Blob([resp.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success(`Backup downloaded (${format.toUpperCase()})`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Backup failed");
    } finally { setDownloading(false); }
  };

  const restoreBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const first = window.confirm(
      "This will WIPE every collection and REPLACE it with the contents of the backup file.\n\n" +
      "All current data (users, bookings, treatments, guests, photos) will be lost.\n\n" +
      "Do you want to continue?"
    );
    if (!first) { if (restoreInputRef.current) restoreInputRef.current.value = ""; return; }
    const typed = window.prompt('Type RESTORE (uppercase) to confirm the wipe.');
    if (typed !== "RESTORE") { toast.error("Restore cancelled — confirmation text did not match."); if (restoreInputRef.current) restoreInputRef.current.value = ""; return; }
    setRestoring(true); setRestoreResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("confirm", "WIPE-AND-RESTORE");
      const { data } = await api.post("/admin/platform/backup/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRestoreResult(data);
      toast.success("Backup restored — you may need to sign in again");
      loadHealth();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Restore failed");
    } finally {
      setRestoring(false);
      if (restoreInputRef.current) restoreInputRef.current.value = "";
    }
  };

  if (loading && !health) return <div className="text-sm text-muted-foreground">Loading platform diagnostics…</div>;

  const db = health?.database || {};
  const sys = health?.system || {};
  const email = health?.email || {};
  const pulse = health?.bookings_pulse || {};

  return (
    <div className="space-y-8" data-testid="platform-panel">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h2 className="font-display text-3xl flex items-center gap-3"><Activity className="h-6 w-6 text-accent" /> Platform tools</h2>
          <p className="text-sm text-muted-foreground mt-1">Developer-only diagnostics · fine-tune and keep the app healthy.</p>
        </div>
        <Button variant="outline" onClick={loadHealth} className="rounded-full" data-testid="platform-refresh">
          <RefreshCw className="h-3 w-3 mr-2" /> Refresh
        </Button>
      </div>

      <VoiceTranslationSettings />

      {/* Journey-map switches — Developer control over the two guest-facing
          maps: the circular per-stop dial and the full-route Leaflet popup. */}
      <section data-testid="map-switches">
        <div className="overline text-accent mb-3 flex items-center gap-2">
          <MapIcon className="h-3 w-3" /> Journey maps
        </div>
        <div className="bg-card border border-border p-5 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <Label className="font-medium flex items-center gap-2">
                <MapIcon className="h-4 w-4 text-accent" /> Circular stop map
              </Label>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                The golden-dial popup that opens when a guest taps a stop on the itinerary strip.
                Turn off to make stops read-only labels — no map dialog will mount anywhere in the app.
              </p>
            </div>
            <Switch
              checked={portMapOn}
              disabled={mapSwitchesSaving}
              onCheckedChange={(v) => saveMapSwitch({ journey_port_map_enabled: !!v })}
              data-testid="map-switch-port"
            />
          </div>
          <div className="h-px bg-border" />
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <Label className="font-medium flex items-center gap-2">
                <Route className="h-4 w-4 text-accent" /> Full route map
              </Label>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                The "View route on map" button + Leaflet popup with sea-route polylines between every stop.
                Turn off to completely restrict access — the button hides and the dialog never mounts.
              </p>
            </div>
            <Switch
              checked={routeMapOn}
              disabled={mapSwitchesSaving}
              onCheckedChange={(v) => saveMapSwitch({ journey_route_map_enabled: !!v })}
              data-testid="map-switch-route"
            />
          </div>
          <div className="h-px bg-border" />
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <Label className="font-medium flex items-center gap-2">
                Mask guest names for Management Company
              </Label>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                When on, any `management_company` account sees guests as
                <code className="mx-1 px-1 bg-muted">VIP no:1 Male</code> style aliases instead of real names.
                Regular admin / manager / crew are always unaffected.
              </p>
            </div>
            <Switch
              checked={!!settings?.mgmt_mask_names}
              disabled={mapSwitchesSaving}
              onCheckedChange={(v) => saveMapSwitch({ mgmt_mask_names: !!v })}
              data-testid="mgmt-mask-switch"
            />
          </div>
        </div>
      </section>

      {/* System */}
      <section>
        <div className="overline text-accent mb-3 flex items-center gap-2"><Server className="h-3 w-3" /> System</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Python" value={sys.python || "—"} testid="stat-python" />
          <StatCard label="Uptime" value={fmtSecs(sys.uptime_seconds)} testid="stat-uptime" />
          <StatCard label="Platform" value={(sys.platform || "").split("-")[0] || "—"} />
          <StatCard label="Storage" value={health?.storage?.initialized ? "OK" : "Not initialized"} tone={health?.storage?.initialized ? "good" : "warn"} />
        </div>
      </section>

      {/* Database */}
      <section>
        <div className="overline text-accent mb-3 flex items-center gap-2"><Database className="h-3 w-3" /> Database</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Status" value={db.ok ? "Healthy" : "Down"} tone={db.ok ? "good" : "bad"} testid="stat-db-status" />
          <StatCard label="Ping" value={db.latency_ms != null ? `${db.latency_ms} ms` : "—"} tone={db.latency_ms > 200 ? "warn" : "default"} />
          <StatCard label="MongoDB" value={db.version || "—"} />
          <StatCard label="Host" value={(db.host || "").split(":")[0] || "—"} />
        </div>
        {!db.ok && db.error && (
          <div className="mt-3 border border-destructive/40 bg-destructive/5 p-3 text-xs" data-testid="stat-db-error">
            <div className="overline text-destructive/70 text-[10px] mb-1">Diagnostic</div>
            <div className="font-mono whitespace-pre-wrap text-destructive/90">{db.error}</div>
            <p className="mt-2 text-muted-foreground">
              If this only happens on a managed MongoDB (for example Atlas): the app's DB user usually can't call
              <span className="font-mono"> serverStatus</span>. The health check now uses low-privilege
              <span className="font-mono"> ping</span>, so if you still see "Down", the DB is truly unreachable — check the connection string / firewall.
            </p>
          </div>
        )}
        <div className="mt-4 border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr><th className="p-3 text-left overline text-primary/50 font-normal">Collection</th><th className="p-3 text-right overline text-primary/50 font-normal">Documents</th></tr>
            </thead>
            <tbody>
              {Object.entries(db.collections || {}).map(([name, count]) => (
                <tr key={name} className="border-t border-border" data-testid={`col-row-${name}`}>
                  <td className="p-3 font-mono text-xs">{name}</td>
                  <td className="p-3 text-right font-mono">{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Bookings pulse + email */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="overline text-accent mb-3 flex items-center gap-2"><HardDrive className="h-3 w-3" /> Bookings pulse</div>
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Total" value={pulse.total ?? "—"} />
            <StatCard label="Upcoming" value={pulse.upcoming ?? "—"} />
            <StatCard label="Cancelled" value={pulse.cancelled ?? "—"} />
            <StatCard label="Reminders sent" value={pulse.reminders_sent ?? "—"} />
          </div>
        </div>
        <div>
          <div className="overline text-accent mb-3 flex items-center gap-2"><Mail className="h-3 w-3" /> Email transport</div>
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Enabled" value={email.enabled ? "Yes" : "No"} tone={email.enabled ? "good" : "warn"} />
            <StatCard label="Transport" value={email.transport || "—"} tone={email.transport === "none" ? "bad" : "default"} testid="stat-email-transport" />
            <StatCard label="Reminder" value={email.reminder_min_before ? `${email.reminder_min_before} min` : "off"} />
            <StatCard label="Notify list" value={email.notify_recipients || 0} />
          </div>
        </div>
      </section>

      {/* Backend logs */}
      <section>
        <button
          className="w-full flex items-center justify-between p-3 border border-border bg-card hover:bg-muted/50 transition-colors"
          onClick={() => setLogsOpen((v) => !v)}
          data-testid="platform-logs-toggle"
        >
          <span className="flex items-center gap-2 overline text-accent">
            {logsOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Backend logs · tail
          </span>
          <span className="text-xs text-muted-foreground">{logs.length ? `${logs.length} lines` : "click to load"}</span>
        </button>
        {logsOpen && (
          <div className="mt-3 space-y-3">
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <Label className="overline text-primary/60 text-[10px]">Level</Label>
                <Select value={logLevel} onValueChange={setLogLevel}>
                  <SelectTrigger className="mt-1 w-40" data-testid="platform-log-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="error">Errors only</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-primary/60 text-[10px]">Lines</Label>
                <Input type="number" min="20" max="2000" value={logLines} onChange={(e) => setLogLines(Number(e.target.value) || 200)} className="mt-1 w-24" />
              </div>
              <Button variant="outline" onClick={loadLogs} disabled={logLoading} className="rounded-full" data-testid="platform-log-refresh">
                <RefreshCw className={`h-3 w-3 mr-2 ${logLoading ? "animate-spin" : ""}`} /> Refresh logs
              </Button>
            </div>
            <div className="bg-primary text-primary-foreground font-mono text-[11px] p-4 max-h-96 overflow-y-auto border border-primary" data-testid="platform-log-viewer">
              {logs.length === 0 ? (
                <div className="text-primary-foreground/60">No log entries.</div>
              ) : logs.map((l, i) => (
                // Log lines are appended in order and don't have server-side
                // ids — file+line-preview+index is a stable composite key.
                <div key={`${l.file}-${i}-${(l.line || "").slice(0, 32)}`} className={l.line.toLowerCase().includes("error") ? "text-red-300" : ""}>
                  <span className="opacity-40">{l.file} · </span>{l.line}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Guest connection activity (Developer feed) */}
      <section data-testid="guest-activity-section">
        <div className="overline text-accent mb-3 flex items-center gap-2"><Smartphone className="h-3 w-3" /> Guest connection activity</div>
        <div className="bg-card border border-border">
          <div className="p-4 border-b border-border flex flex-wrap gap-2 items-center justify-between">
            <div className="flex flex-wrap gap-2 items-center text-xs">
              <span className="text-muted-foreground">Total guests</span>
              <span className="font-mono">{activityCounts.total}</span>
              <span className="text-muted-foreground">· connected</span>
              <span className="font-mono text-emerald-600 dark:text-emerald-400">{activityCounts.connected}</span>
              <span className="text-muted-foreground">· with mobile</span>
              <span className="font-mono text-accent">{activityCounts.mobile}</span>
              <span className="text-muted-foreground">· iPad-only</span>
              <span className="font-mono">{activityCounts.ipad_only}</span>
            </div>
            <div className="flex gap-2 items-center">
              <div className="flex gap-1" data-testid="activity-filter">
                {[
                  { k: "all", label: "All" },
                  { k: "connected", label: "Connected" },
                  { k: "mobile", label: "On mobile" },
                  { k: "never", label: "Never signed in" },
                ].map(({ k, label }) => (
                  <button
                    key={k}
                    onClick={() => setActivityFilter(k)}
                    className={`text-xs px-2.5 py-1 border rounded-full ${activityFilter === k ? "border-accent bg-accent text-accent-foreground" : "border-border hover:border-accent/50"}`}
                    data-testid={`activity-filter-${k}`}
                  >{label}</button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={loadActivity} disabled={activityLoading} className="rounded-full" data-testid="activity-refresh">
                <RefreshCw className={`h-3 w-3 mr-1.5 ${activityLoading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="guest-activity-table">
              <thead className="bg-muted/40 sticky top-0">
                <tr className="text-left">
                  <th className="p-3 overline text-primary/50 font-normal">Guest</th>
                  <th className="p-3 overline text-primary/50 font-normal">Cabin · Charter</th>
                  <th className="p-3 overline text-primary/50 font-normal">Last seen</th>
                  <th className="p-3 overline text-primary/50 font-normal">How</th>
                  <th className="p-3 overline text-primary/50 font-normal text-right">Logins</th>
                  <th className="p-3 overline text-primary/50 font-normal">IP</th>
                </tr>
              </thead>
              <tbody>
                {filteredActivity.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No guests match this filter.</td></tr>
                )}
                {filteredActivity.map((a) => (
                  <tr key={`${a.charter_id}:${a.guest_id}`} className="border-t border-border" data-testid={`activity-row-${a.guest_id}`}>
                    <td className="p-3">
                      <div className="font-medium flex items-center gap-2">
                        {a.guest_name || "Unnamed"}
                        {a.has_mobile && <Smartphone className="h-3 w-3 text-accent" />}
                        {a.last_login_kind === "ipad" && !a.has_mobile && <Wifi className="h-3 w-3 text-primary/50" />}
                      </div>
                      <div className="text-[10px] text-muted-foreground font-mono">{(a.guest_id || "").slice(0, 8)}…</div>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">
                      <div>{a.cabin_name || "—"}</div>
                      <div className="text-[10px]">{a.charter_name} <span className={a.charter_active ? "text-emerald-600" : "text-muted-foreground/60"}>· {a.charter_status}</span></div>
                    </td>
                    <td className="p-3 text-xs font-mono">{fmtWhen(a.last_login_at)}</td>
                    <td className="p-3">{kindBadge(a.last_login_kind, a.last_login_device)}</td>
                    <td className="p-3 text-right font-mono">{a.login_count || 0}</td>
                    <td className="p-3 text-[10px] font-mono text-muted-foreground truncate max-w-[120px]" title={a.last_login_ua || ""}>{a.last_login_ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Backup & Restore */}
      <section data-testid="backup-restore-section">
        <div className="overline text-accent mb-3 flex items-center gap-2"><ShieldAlert className="h-3 w-3" /> Backup & Restore</div>
        <div className="bg-card border border-border p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="font-display text-xl mb-2 flex items-center gap-2"><Download className="h-4 w-4 text-accent" /> Full backup</div>
            <p className="text-xs text-muted-foreground leading-relaxed mb-4">
              Downloads a snapshot of every collection (<span className="font-mono">users, settings, cabins, treatments, categories, crew, charters, bookings, files, bug reports</span>) <b>plus the raw bytes of every uploaded image</b>. Pick <span className="font-mono">ZIP</span> for a ~4× smaller archive (raw files, no base64 bloat) or the classic <span className="font-mono">JSON</span> for a single self-contained file.
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => downloadBackup("zip")}
                disabled={downloading}
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid="backup-download-zip-btn"
                title="Smaller archive — raw file bytes, no base64. Recommended."
              >
                <Download className="h-3.5 w-3.5 mr-2" />
                {downloading ? "Preparing…" : "Download backup (ZIP)"}
              </Button>
              <Button
                onClick={() => downloadBackup("json")}
                disabled={downloading}
                variant="outline"
                className="rounded-full"
                data-testid="backup-download-btn"
                title="Legacy JSON — single self-contained file with base64-encoded images."
              >
                {downloading ? "Preparing…" : "JSON"}
              </Button>
            </div>
          </div>
          <div>
            <div className="font-display text-xl mb-2 flex items-center gap-2 text-destructive"><Upload className="h-4 w-4" /> Restore from backup</div>
            <p className="text-xs text-muted-foreground leading-relaxed mb-4">
              <b className="text-destructive">DESTRUCTIVE.</b> Wipes every current collection and re-inserts the snapshot's contents, then re-uploads every image. Requires typing <span className="font-mono">RESTORE</span> to confirm.
            </p>
            <div className="flex gap-2 items-center">
              <input
                ref={restoreInputRef}
                type="file"
                accept="application/json,.json,.zip,application/zip"
                onChange={restoreBackup}
                className="hidden"
                data-testid="backup-restore-input"
                disabled={restoring}
              />
              <Button
                onClick={() => restoreInputRef.current?.click()}
                disabled={restoring}
                variant="outline"
                className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/5"
                data-testid="backup-restore-btn"
              >
                <Upload className="h-3.5 w-3.5 mr-2" />
                {restoring ? "Restoring…" : "Choose backup file…"}
              </Button>
            </div>
            {restoreResult && (
              <div className="mt-4 border border-emerald-500/40 bg-emerald-500/5 p-3 text-xs" data-testid="restore-result">
                <div className="font-medium mb-1 text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Restore complete
                </div>
                <div className="text-muted-foreground">
                  Files re-uploaded: <span className="font-mono">{restoreResult.files_restored}</span>
                  {restoreResult.files_failed ? <> · failed: <span className="font-mono text-destructive">{restoreResult.files_failed}</span></> : null}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono">
                  {Object.entries(restoreResult.collections || {}).map(([k, v]) => (
                    <div key={k}><span className="text-muted-foreground">{k}:</span> {String(v)}</div>
                  ))}
                </div>
                <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-400">{restoreResult.warning}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bug reports */}
      <section>
        <div className="overline text-accent mb-3 flex items-center gap-2"><Bug className="h-3 w-3" /> Bug reports</div>
        <div className="bg-card border border-border p-5 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px] gap-3">
            <div>
              <Label className="overline text-primary/60 text-[10px]">Title</Label>
              <Input value={bugForm.title} onChange={(e) => setBugForm({ ...bugForm, title: e.target.value })} placeholder="What went wrong?" data-testid="bug-title" className="mt-1" />
            </div>
            <div>
              <Label className="overline text-primary/60 text-[10px]">Description</Label>
              <Input value={bugForm.description} onChange={(e) => setBugForm({ ...bugForm, description: e.target.value })} placeholder="Steps to reproduce…" data-testid="bug-desc" className="mt-1" />
            </div>
            <div>
              <Label className="overline text-primary/60 text-[10px]">Severity</Label>
              <Select value={bugForm.severity} onValueChange={(v) => setBugForm({ ...bugForm, severity: v })}>
                <SelectTrigger className="mt-1" data-testid="bug-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={submitBug} disabled={creatingBug} className="rounded-full bg-primary hover:bg-primary/90" data-testid="bug-submit">
              {creatingBug ? "Filing…" : "File report"}
            </Button>
          </div>
        </div>
        <div className="border border-border" data-testid="bug-list">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="p-3 text-left overline text-primary/50 font-normal">Reported</th>
                <th className="p-3 text-left overline text-primary/50 font-normal">Title</th>
                <th className="p-3 text-left overline text-primary/50 font-normal">By</th>
                <th className="p-3 text-left overline text-primary/50 font-normal">Sev</th>
                <th className="p-3 text-left overline text-primary/50 font-normal">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {bugs.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`bug-row-${r.id}`}>
                  <td className="p-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="p-3">
                    <div className="font-medium">{r.title}</div>
                    {r.description && <div className="text-xs text-muted-foreground line-clamp-2 max-w-md">{r.description}</div>}
                  </td>
                  <td className="p-3 text-xs">{r.reported_by} · {r.reporter_role}</td>
                  <td className="p-3">
                    <Badge variant="outline" className={r.severity === "high" ? "border-destructive/50 text-destructive" : r.severity === "medium" ? "border-amber-500/50 text-amber-700 dark:text-amber-400" : ""}>
                      {r.severity}
                    </Badge>
                  </td>
                  <td className="p-3">
                    {r.status === "closed" ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-3 w-3" /> closed</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400"><AlertTriangle className="h-3 w-3" /> open</span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-2">
                      {r.status !== "closed" && (
                        <Button size="sm" variant="outline" onClick={() => closeBug(r)} data-testid={`bug-close-${r.id}`}>Close</Button>
                      )}
                      <Button size="sm" variant="outline" className="text-destructive border-destructive/40" onClick={() => deleteBug(r)} data-testid={`bug-del-${r.id}`}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {bugs.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-sm">No bug reports yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* MQTT push (Developer only — the whole PlatformToolsPanel is already dev-gated by the router) */}
      <MqttPanel />

      {/* Feature analytics — per-iPad and per-crew usage insights */}
      <AnalyticsPanel />

      {/* Live diagnostics — metrics, event log, MQTT sniffer */}
      <DiagnosticsPanel />
    </div>
  );
}

